import { ApiError, syncOfflineOrders as postOfflineSync } from "./api";
import {
  buildQueuedOrder,
  type BuildOfflineOrderInput,
} from "./offlineOrderDocument";
import {
  enqueue,
  readAll,
  recordFailure,
  removeConfirmed,
  type QueuedOrder,
} from "./offlineStore";
import { relayOfflineOrderToNative } from "@/utils/webview";

/**
 * THE FLUSH — PRODUCT CHANGE §2 (`DEVIATIONS.md`).
 *
 * Modelled on the recovered POS's sync
 * (`pos-recovered/src/features/offlineOrders/offlineOrdersThunks.js` and
 * `services/socket/reservationSocketHandlers.js`), because its two hard-won
 * rules are the ones that make a partial failure safe:
 *
 * 1. **Clear ONLY the ids the server confirmed.** The endpoint returns the
 *    stored documents, and only the ones that persisted. An order the server
 *    rejected is not in that array, so it is not deleted and is still queued
 *    for the next attempt. Clearing the batch on a 200 would silently destroy
 *    every order that failed inside it.
 * 2. **Per order, not per batch.** The server already isolates each order in
 *    its own try/catch, so one bad document cannot abort the rest. This side
 *    matches: a batch that returns fewer rows than it sent is a normal partial
 *    success, not an error.
 *
 * **Batch size 10**, the POS's own cap (`orderIds.slice(0, 10)`), which the
 * server's comment names as the contract. The request body limit is 15 MB and
 * an order with `iterations` is not small.
 */

const BATCH_SIZE = 10;

export interface SyncOutcome {
  attempted: number;
  confirmed: number;
  /** What is still queued afterwards. */
  remaining: QueuedOrder[];
  error?: string | null;
}

let inFlight: Promise<SyncOutcome> | null = null;

/**
 * Is this failure "the network is not there", as opposed to "the server said
 * no"?
 *
 * It decides whether an order is QUEUED or REFUSED, so it must not be
 * generous. A 409 ("we do not deliver to that address", "that table already
 * has an open order") or a 400 is the server making a decision, and queueing
 * it would mean replaying a refusal later as if it had been accepted.
 *
 * Queued cases:
 *  - `navigator.onLine === false`;
 *  - a thrown `TypeError` from `fetch` — the whole "failed to fetch" family:
 *    DNS, refused connection, dropped Wi-Fi, rejected certificate;
 *  - **502 / 503 from this app's own proxy**. `src/pages/api/backend/[...path].ts`
 *    answers `502 { error: "Ordering API unreachable" }` when it cannot reach
 *    :4001 and `503` when no API is configured. Those are OUR envelope around
 *    an unreachable API, not the API's own answer, and they are the shape a
 *    remote visitor actually sees when the shop's link drops — the browser is
 *    still talking to :3002 quite happily.
 *  - 504, for a gateway in front of either.
 */
export const isConnectivityFailure = (error: unknown): boolean => {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return true;
  }
  if (error instanceof ApiError) {
    return error.status === 502 || error.status === 503 || error.status === 504;
  }
  // `fetch` rejects with a TypeError for every transport-level failure.
  return error instanceof TypeError;
};

/** Put one order in the queue. Returns the whole queue afterwards. */
export const queueOrderOffline = async (
  input: BuildOfflineOrderInput,
): Promise<{ order: QueuedOrder; queue: QueuedOrder[] }> => {
  const order = buildQueuedOrder(input);
  const queue = await enqueue(order);
  // Native Staff Ordering persists this same finished document before sending
  // it to the paired POS terminal. The browser queue remains authoritative
  // until cloud sync confirms it, so closing either app cannot lose the sale.
  if (order.lan_relay_enabled) relayOfflineOrderToNative(order.document);
  return { order, queue };
};

export const readQueue = (): Promise<QueuedOrder[]> => readAll();

/**
 * Send everything that is queued, tenant by tenant, ten at a time.
 *
 * Re-entrant by design: the boot pass, the `online` event and the customer
 * pressing "Send now" can all land at once, and a second concurrent flush
 * would post the same documents twice. The upsert makes that harmless on the
 * server, but it would double the work and confuse the counters, so the
 * in-flight promise is shared instead.
 */
export const flushOfflineOrders = async (): Promise<SyncOutcome> => {
  if (inFlight) return inFlight;

  inFlight = (async (): Promise<SyncOutcome> => {
    let queue = await readAll();
    if (queue.length === 0) {
      return { attempted: 0, confirmed: 0, remaining: [] };
    }
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return {
        attempted: 0,
        confirmed: 0,
        remaining: queue,
        error: "still offline",
      };
    }

    let attempted = 0;
    let confirmed = 0;
    let lastError: string | null = null;

    const tenants = [...new Set(queue.map((row) => row.tenant_id))];

    for (const tenantId of tenants) {
      // Loop while the previous batch made progress. A batch that confirms
      // nothing stops this tenant rather than spinning on the same ten rows.
      for (;;) {
        const pending = queue
          .filter((row) => row.tenant_id === tenantId)
          .slice(0, BATCH_SIZE);
        if (pending.length === 0) break;

        attempted += pending.length;
        try {
          const stored = await postOfflineSync(
            tenantId,
            pending.map((row) => row.document),
          );
          const confirmedIds = stored
            .map((row) => (row?._id == null ? "" : String(row._id)))
            .filter(Boolean);

          queue = await removeConfirmed(confirmedIds);
          confirmed += confirmedIds.length;

          const rejected = pending
            .filter((row) => !confirmedIds.includes(row._id))
            .map((row) => row._id);
          if (rejected.length > 0) {
            queue = await recordFailure(
              rejected,
              "the server did not confirm this order",
            );
            lastError = `${rejected.length} order(s) were not accepted`;
            // No progress on these rows; stop this tenant rather than resend
            // the same batch in a loop.
            break;
          }
        } catch (error) {
          const message =
            error instanceof ApiError
              ? failureMessage(error)
              : error instanceof Error
                ? error.message
                : "sync failed";
          lastError = message;
          queue = await recordFailure(
            pending.map((row) => row._id),
            message,
          );
          break;
        }
      }
    }

    return { attempted, confirmed, remaining: queue, error: lastError };
  })();

  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
};

/**
 * The endpoint's all-failed answer is `400 { message, failed: [{_id, error}] }`;
 * `ApiError.body` holds it. Reading the server's own words matters here — the
 * one failure this endpoint used to give on every call was a `menu_id` cast
 * error, and a generic "sync failed" would have hidden it.
 */
const failureMessage = (error: ApiError): string => {
  const body = error.body;
  if (body && typeof body === "object") {
    const message = (body as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }
  if (typeof body === "string" && body.trim()) return body;
  return error.message;
};
